import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Globalni context-aware help widget — dva floating gumba (📖 Upute, 💬 Komentari).
 * Kontekst = aktivni modul + (opcionalno tab iz URL ?tab=). Fallback na modul-level.
 *
 *  - Upute: PDF u layover-u (browser native zoom). Superadmin: upload (gazi staro) + obriši.
 *  - Komentari (samo superadmin): floating resizable panel — collab doc (autosave) + chat.
 */
type Props = {
    activeModule: string;
    token: string;
    locale: 'hr' | 'en';
    isSuperadmin: boolean;
};

function useContextKey(activeModule: string): string {
    const [tab, setTab] = useState<string>(() => new URLSearchParams(window.location.search).get('tab') ?? '');
    useEffect(() => {
        const read = () => setTab(new URLSearchParams(window.location.search).get('tab') ?? '');
        window.addEventListener('popstate', read);
        // URL se mijenja i bez popstate (pushState) — lagani interval
        const id = window.setInterval(read, 1500);
        return () => { window.removeEventListener('popstate', read); window.clearInterval(id); };
    }, []);
    return tab ? `${activeModule}/${tab}` : activeModule;
}

async function authFetch(path: string, token: string, init?: RequestInit): Promise<Response> {
    return fetch(`/api${path}`, {
        ...init,
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, ...(init?.headers ?? {}) },
    });
}

export function ModuleHelpWidget({ activeModule, token, locale, isSuperadmin }: Props) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const context = useContextKey(activeModule);
    const [showUpute, setShowUpute] = useState(false);
    const [showKom, setShowKom] = useState(false);

    return (
        <>
            {/* Slagani IZNAD AI fab-a (koji je right:20 bottom:20 width:58, z-index:1200) da se ne sudaraju. */}
            <div style={{ position: 'fixed', right: 26, bottom: 88, zIndex: 1150, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button type="button" onClick={() => setShowUpute(true)} title={t('Upute za ovaj modul', 'Module instructions')}
                    style={{ width: 46, height: 46, borderRadius: '50%', border: 0, background: 'linear-gradient(135deg,var(--ad-ink),var(--ad-acc))', color: '#fff', fontSize: '1.25rem', cursor: 'pointer', boxShadow: '0 3px 10px rgba(107,79,28,0.35)' }}>📖</button>
                {isSuperadmin ? (
                    <button type="button" onClick={() => setShowKom(true)} title={t('Komentari / collaborate (superadmin)', 'Comments / collaborate')}
                        style={{ width: 46, height: 46, borderRadius: '50%', border: 0, background: 'linear-gradient(135deg,#1e40af,#3b82f6)', color: '#fff', fontSize: '1.2rem', cursor: 'pointer', boxShadow: '0 3px 10px rgba(30,64,175,0.35)' }}>💬</button>
                ) : null}
            </div>
            {showUpute ? <UputeModal context={context} token={token} locale={locale} isSuperadmin={isSuperadmin} onClose={() => setShowUpute(false)} /> : null}
            {showKom && isSuperadmin ? <KomentariPanel context={context} token={token} locale={locale} onClose={() => setShowKom(false)} /> : null}
        </>
    );
}

/* ── UPUTE (PDF) ─────────────────────────────────────────────────────────── */
function UputeModal({ context, token, locale, isSuperadmin, onClose }: { context: string; token: string; locale: 'hr' | 'en'; isSuperadmin: boolean; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [meta, setMeta] = useState<any>(null);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const fileRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setErr(''); setPdfUrl(null);
        try {
            const r = await authFetch(`/module-help/instructions?context=${encodeURIComponent(context)}`, token);
            const j = await r.json();
            setMeta(j);
            if (j.has_pdf) {
                const pr = await authFetch(`/module-help/instructions/pdf?context=${encodeURIComponent(context)}`, token);
                if (pr.ok) { const blob = await pr.blob(); setPdfUrl(URL.createObjectURL(blob)); }
            }
        } catch (e) { setErr(e instanceof Error ? e.message : 'API'); }
        finally { setLoading(false); }
    }, [context, token]);
    useEffect(() => { void load(); }, [load]);
    useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

    const upload = async () => {
        const f = fileRef.current?.files?.[0];
        if (!f) return;
        if (!confirm(t('⚠ Ovo će PREGAZITI postojeće upute za ovaj kontekst — stare će biti izbrisane. Nastaviti?', '⚠ This will OVERWRITE existing instructions. Continue?'))) return;
        setUploading(true); setErr('');
        try {
            const fd = new FormData(); fd.append('context', context); fd.append('file', f);
            const r = await authFetch('/module-help/instructions', token, { method: 'POST', body: fd });
            if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j?.message ?? `HTTP ${r.status}`); }
            await load();
        } catch (e) { setErr(e instanceof Error ? e.message : 'Upload fail'); }
        finally { setUploading(false); }
    };

    const del = async () => {
        if (!confirm(t('Obrisati upute za ovaj kontekst?', 'Delete instructions?'))) return;
        try { await authFetch(`/module-help/instructions?context=${encodeURIComponent(context)}`, token, { method: 'DELETE' }); await load(); }
        catch (e) { setErr(e instanceof Error ? e.message : 'API'); }
    };

    return (
        <div role="dialog" aria-modal="true" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(1000px, 96vw)', height: '92vh', background: '#fff', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: 'linear-gradient(135deg,var(--ad-ink),var(--ad-acc))', color: '#fff', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <strong style={{ fontSize: '1rem' }}>📖 {t('Upute', 'Instructions')}</strong>
                    <span style={{ fontSize: '0.78rem', opacity: 0.85 }}>{context}{meta?.fallback_from ? ` (${t('modul-level', 'module-level')})` : ''}</span>
                    {meta?.has_pdf ? <span style={{ fontSize: '0.74rem', opacity: 0.8, marginLeft: 'auto' }}>{meta.filename} · {meta.uploaded_at ? new Date(meta.uploaded_at).toLocaleDateString('hr-HR') : ''}</span> : null}
                    <button type="button" onClick={onClose} style={{ marginLeft: meta?.has_pdf ? 12 : 'auto', background: 'rgba(255,255,255,0.2)', color: '#fff', border: 0, borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: '1.05rem' }}>✕</button>
                </div>
                {isSuperadmin ? (
                    <div style={{ padding: '8px 16px', background: '#fdfaf2', borderBottom: '1px solid #eee5d0', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input ref={fileRef} type="file" accept="application/pdf" style={{ fontSize: '0.8rem' }} />
                        <button type="button" onClick={() => void upload()} disabled={uploading} style={{ background: '#1f7a36', color: '#fff', border: 0, borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem' }}>{uploading ? '⏳' : '⬆'} {t('Osvježi upute', 'Update instructions')}</button>
                        {meta?.has_pdf ? <button type="button" onClick={() => void del()} className="secondary compact" style={{ fontSize: '0.76rem', color: '#b14040' }}>🗑 {t('Obriši', 'Delete')}</button> : null}
                        <span style={{ fontSize: '0.72rem', color: 'var(--ad-acc-3)' }}>{t('Upload gazi stare upute. Tekst PDF-a postaje izvor istine za AI.', 'Upload overwrites. PDF text becomes AI source of truth.')}</span>
                    </div>
                ) : null}
                <div style={{ flex: 1, background: '#525659', minHeight: 0 }}>
                    {loading ? <p style={{ color: '#fff', padding: 20 }}>{t('Učitavam…', 'Loading…')}</p>
                    : err ? <p style={{ color: '#ffb4b4', padding: 20 }}>{err}</p>
                    : pdfUrl ? <iframe title="upute" src={pdfUrl} style={{ width: '100%', height: '100%', border: 0 }} />
                    : <div style={{ color: '#ddd', padding: 40, textAlign: 'center' }}>
                        <p style={{ fontSize: '1.1rem' }}>📭 {t('Za ovaj modul/tab još nema uputa.', 'No instructions for this module/tab yet.')}</p>
                        {isSuperadmin ? <p style={{ fontSize: '0.85rem', opacity: 0.8 }}>{t('Uploadaj PDF gore.', 'Upload a PDF above.')}</p> : <p style={{ fontSize: '0.85rem', opacity: 0.8 }}>{t('Obrati se superadministratoru.', 'Ask a superadmin.')}</p>}
                    </div>}
                </div>
            </div>
        </div>
    );
}

/* ── KOMENTARI + COLLAB (superadmin) ─────────────────────────────────────── */
function KomentariPanel({ context, token, locale, onClose }: { context: string; token: string; locale: 'hr' | 'en'; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [comments, setComments] = useState<any[]>([]);
    const [collab, setCollab] = useState('');
    const [collabMeta, setCollabMeta] = useState<{ updated_by?: string; updated_at?: string }>({});
    const [newComment, setNewComment] = useState('');
    const [people, setPeople] = useState<{ id: number; name: string }[]>([]);
    const [mentionIds, setMentionIds] = useState<number[]>([]);
    const [mentionQuery, setMentionQuery] = useState<string | null>(null); // null = @-menu zatvoren
    const collabTimer = useRef<number | null>(null);
    const editingCollab = useRef(false);

    useEffect(() => {
        authFetch('/module-help/chat-people', token).then((r) => r.json()).then((j) => setPeople(j.people ?? [])).catch(() => { /* */ });
    }, [token]);

    const loadComments = useCallback(async () => {
        try { const r = await authFetch(`/module-help/comments?context=${encodeURIComponent(context)}`, token); const j = await r.json(); setComments(j.rows ?? []); } catch { /* */ }
    }, [context, token]);
    const loadCollab = useCallback(async () => {
        try {
            const r = await authFetch(`/module-help/collab?context=${encodeURIComponent(context)}`, token); const j = await r.json();
            if (!editingCollab.current) setCollab(j.body ?? '');
            setCollabMeta({ updated_by: j.updated_by, updated_at: j.updated_at });
        } catch { /* */ }
    }, [context, token]);

    useEffect(() => { void loadComments(); void loadCollab(); }, [loadComments, loadCollab]);
    // Poll svakih 8s (pragmatični "realtime")
    useEffect(() => {
        const id = window.setInterval(() => { void loadComments(); void loadCollab(); }, 8000);
        return () => window.clearInterval(id);
    }, [loadComments, loadCollab]);

    const saveCollab = (body: string) => {
        setCollab(body); editingCollab.current = true;
        if (collabTimer.current) window.clearTimeout(collabTimer.current);
        collabTimer.current = window.setTimeout(async () => {
            try { await authFetch('/module-help/collab', token, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ context, body }) }); }
            catch { /* */ }
            finally { editingCollab.current = false; void loadCollab(); }
        }, 900);
    };

    const onInputChange = (val: string) => {
        setNewComment(val);
        const at = val.lastIndexOf('@');
        if (at === -1 || val.slice(at).includes('\n')) { setMentionQuery(null); return; }
        setMentionQuery(val.slice(at + 1));
    };
    const pickMention = (p: { id: number; name: string }) => {
        const at = newComment.lastIndexOf('@');
        const before = at >= 0 ? newComment.slice(0, at) : newComment;
        setNewComment(`${before}@${p.name} `);
        setMentionIds((ids) => ids.includes(p.id) ? ids : [...ids, p.id]);
        setMentionQuery(null);
    };
    const mentionMatches = mentionQuery !== null
        ? people.filter((p) => p.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6)
        : [];

    const send = async () => {
        if (!newComment.trim()) return;
        // Zadrži samo tagove čije ime još postoji u tekstu (ako je user obrisao @Ime).
        const keptMentions = mentionIds.filter((id) => {
            const p = people.find((x) => x.id === id);
            return p && newComment.includes('@' + p.name);
        });
        try {
            await authFetch('/module-help/comments', token, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ context, body: newComment.trim(), mentions: keptMentions }) });
            setNewComment(''); setMentionIds([]); setMentionQuery(null); await loadComments();
        } catch { /* */ }
    };

    const removeComment = async (id: number) => {
        if (!confirm(t('Obrisati poruku?', 'Delete message?'))) return;
        try {
            await authFetch(`/module-help/comments/${id}`, token, { method: 'DELETE' });
            await loadComments();
        } catch { /* */ }
    };

    return (
        <div style={{
            position: 'fixed', right: 20, bottom: 150, zIndex: 1180,
            width: 380, height: 540, minWidth: 300, minHeight: 320, maxWidth: '92vw', maxHeight: '88vh',
            resize: 'both', overflow: 'hidden',
            background: '#fff', border: '1px solid rgba(30,64,175,0.35)', borderRadius: 12, boxShadow: '0 8px 30px rgba(30,64,175,0.25)',
            display: 'flex', flexDirection: 'column',
        }}>
            <div style={{ background: 'linear-gradient(135deg,#1e40af,#3b82f6)', color: '#fff', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong style={{ fontSize: '0.9rem' }}>💬 {t('Komentari', 'Comments')}</strong>
                <span style={{ fontSize: '0.72rem', opacity: 0.85 }}>{context}</span>
                <button type="button" onClick={onClose} style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.2)', color: '#fff', border: 0, borderRadius: 6, padding: '2px 10px', cursor: 'pointer' }}>✕</button>
            </div>
            {/* Collab doc */}
            <div style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#1e40af', fontWeight: 700, marginBottom: 3 }}>
                    📝 {t('Zajednički dokument', 'Collaborate doc')}
                    {collabMeta.updated_by ? <span style={{ fontWeight: 400, color: '#888', marginLeft: 6, textTransform: 'none' }}>· {t('zadnje', 'last')}: {collabMeta.updated_by}</span> : null}
                </div>
                <textarea value={collab} onChange={(e) => saveCollab(e.target.value)} onBlur={() => { editingCollab.current = false; }}
                    placeholder={t('Zajedničke bilješke (autosave)…', 'Shared notes (autosave)…')}
                    style={{ width: '100%', minHeight: 60, maxHeight: 140, fontSize: '0.8rem', border: '1px solid #cbd5e1', borderRadius: 6, padding: 6, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
            {/* Chat list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px', minHeight: 0 }}>
                {comments.length === 0 ? <p style={{ color: '#999', fontSize: '0.8rem', fontStyle: 'italic' }}>{t('Još nema komentara.', 'No comments yet.')}</p> : comments.map((c) => {
                    const tagged: number[] = c.mentions ? (typeof c.mentions === 'string' ? (() => { try { return JSON.parse(c.mentions); } catch { return []; } })() : c.mentions) : [];
                    const taggedNames = tagged.map((id: number) => people.find((p) => p.id === id)?.name).filter(Boolean);
                    return (
                        <div key={c.id} style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: '0.7rem', color: '#1e40af', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>{c.user_name ?? '—'} <span style={{ color: '#999', fontWeight: 400 }}>· {c.created_at ? new Date(c.created_at).toLocaleString('hr-HR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span></span>
                                {c.can_delete ? <button type="button" onClick={() => void removeComment(c.id)} title={t('Obriši moju poruku', 'Delete my message')} style={{ marginLeft: 'auto', border: 0, background: 'transparent', color: '#b14040', cursor: 'pointer', fontSize: '0.74rem', padding: 0 }}>🗑</button> : null}
                            </div>
                            <div style={{ fontSize: '0.84rem', background: '#f1f5f9', borderRadius: 8, padding: '5px 9px', whiteSpace: 'pre-wrap', marginTop: 2 }}>{c.body}</div>
                            {taggedNames.length > 0 ? <div style={{ fontSize: '0.68rem', color: '#1f7a36', marginTop: 2 }}>🔔 {taggedNames.map((n) => '@' + n).join(', ')}</div> : null}
                        </div>
                    );
                })}
            </div>
            {/* Input */}
            <div style={{ padding: 8, borderTop: '1px solid #e5e7eb', position: 'relative' }}>
                {mentionMatches.length > 0 ? (
                    <div style={{ position: 'absolute', left: 8, right: 8, bottom: 'calc(100% - 4px)', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, boxShadow: '0 -4px 14px rgba(0,0,0,0.12)', overflow: 'hidden', zIndex: 5 }}>
                        <div style={{ fontSize: '0.64rem', textTransform: 'uppercase', color: '#888', padding: '4px 9px', background: '#f8fafc' }}>{t('Tagiraj kolegu', 'Tag colleague')}</div>
                        {mentionMatches.map((p) => (
                            <button key={p.id} type="button" onClick={() => pickMention(p)} style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, background: 'transparent', padding: '6px 10px', cursor: 'pointer', fontSize: '0.82rem' }}
                                onMouseDown={(e) => e.preventDefault()}>
                                @{p.name}
                            </button>
                        ))}
                    </div>
                ) : null}
                <div style={{ display: 'flex', gap: 6 }}>
                    <input value={newComment} onChange={(e) => onInputChange(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && mentionMatches.length === 0) void send(); if (e.key === 'Escape') setMentionQuery(null); }}
                        placeholder={t('Komentar… @ za tag, Enter za slanje', 'Comment… @ to tag, Enter to send')} style={{ flex: 1, fontSize: '0.84rem', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 8px' }} />
                    <button type="button" onClick={() => void send()} style={{ background: '#1e40af', color: '#fff', border: 0, borderRadius: 6, padding: '0 14px', cursor: 'pointer', fontWeight: 700 }}>➤</button>
                </div>
            </div>
        </div>
    );
}
